import type {ReactElement} from 'react';
import React, { useEffect, useRef} from 'react';
import styles from './prompt-input-field.module.scss';
import type {AsyncVoidFunction} from '@Utils/general.util';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';

interface PromptInputFieldProps {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  isPromptBoxSelected: boolean;
  placeholder: string;
  handleSubmission: AsyncVoidFunction;
}

export function PromptInputField({prompt, onPromptChange, isPromptBoxSelected, placeholder, ...props}: PromptInputFieldProps): ReactElement {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const {bottomShadow} = useTopBottomScrollShadow(textAreaRef, true);
  const MAX_PROMPT_LENGTH = 1000;

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [prompt]);

  useEffect(() => {
    if (isPromptBoxSelected && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
    }
  }, [isPromptBoxSelected]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const input = e.target.value;
    onPromptChange(input);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void props.handleSubmission();
    }
  };

  const getInputField = (): ReactElement => {
    return (
        <textarea
          ref={textAreaRef}
          className={`spacing-p-2 ${styles.inputField} _full-width ${bottomShadow ? styles.bottomShadow : ''}`}
          value={prompt}
          placeholder={placeholder}
          onChange={handlePromptChange}
          onKeyDown={handleKeyDown}
          maxLength={MAX_PROMPT_LENGTH}
        />
    );
  };

  return getInputField();
}
